<template>
    <template v-if="isOnline">
        <Container v-if="!isLoading">
            <ProgramWrapper v-if="!hasError">
                <ProgramItem
                    v-for="post in posts"
                    :key="post.id"
                    :post="post" />
            </ProgramWrapper>
            <div
                v-else
                class="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-4">
                <ErrorIcon class="tw-h-16 tw-w-16 tw-text-red" />
                <p class="tw-max-w-72 tw-text-center">{{ $t('Tv.error') }}</p>
                <Button
                    class="tw-mt-4"
                    @click="init">
                    {{ $t('shared.tryAgain') }}
                </Button>
            </div>
        </Container>
        <Loader v-else />
    </template>
    <Offline v-else />
</template>

<script setup lang="ts">
    import ErrorIcon from '@/components/icons/Error.vue';
    import Loader from './loader.vue';
    import Offline from './offline.vue';
    import { fetchTvPosts } from '@/api/posts';
    import type { WPTvPost } from '@/contentTypes';
    import { useOnline } from '@vueuse/core';

    const $route = useRoute();
    const isOnline = useOnline();
    const isLoading = ref(true);
    const hasError = ref(false);
    const posts = ref<WPTvPost[]>([]);
    const page = ref(1);
    const totalPosts = ref(0);

    async function init() {
        try {
            isLoading.value = true;
            hasError.value = false;

            const { data } = await fetchTvPosts({
                per_page: 100,
                page: page.value,
            });
            totalPosts.value = data.pagination.total || 0;
            if (page.value === 1) {
                posts.value = data.posts || [];
            } else {
                posts.value.push(...data.posts);
            }

            // sort posts by title
            posts.value.sort((a, b) => a.title.localeCompare(b.title));
        } catch (error) {
            console.error('Error fetching TV posts:', error);
            hasError.value = true;
        } finally {
            isLoading.value = false;
        }
    }

    const stopOnlineWatcher = watch(isOnline, (online) => {
        if (online) {
            init();
        }
    });

    const stopRouteWatcher = watch(() => $route.name, (newRoute) => {
        if (newRoute === 'TV24h' && hasError.value) {
            posts.value = [];
            page.value = 1; // Reset page when navigating to TV24h
            init();
        }
    });

    onUnmounted(() => {
        stopOnlineWatcher();
        stopRouteWatcher();
    });

    if (isOnline.value) {
        init();
    }

</script>

<style scoped></style>
